import React from 'react';
import { useDispatch } from 'react-redux';
import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	List,
	ListItemButton,
	ListItemText,
	Typography
} from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { ErrorAlert, useSettings, useUserData, Utils } from 'app/main/components';

interface CreditsafeCompaniesSearchDialogProps {
	/** The Company uid */
	compUid: string;
	/** The Company country */
	country: string;
	/** The Company VAT number */
	vatNumber: string;
	/** Function used to set the Company Creditsafe id */
	setCreditsafeId: React.Dispatch<React.SetStateAction<string>>;
	/** Function used to refresh the page */
	updateRefresh: VoidFunction;
}

export const showCreditsafeCompaniesSearchDialog = (props: CreditsafeCompaniesSearchDialogProps) =>
	openDialog({
		children: <CreditsafeCompaniesSearchDialog {...props} />,
		maxWidth: 'sm',
		fullWidth: true
	});

/**  */
export default function CreditsafeCompaniesSearchDialog(props: CreditsafeCompaniesSearchDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const userData = useUserData();

	const [companies, setCompanies] = React.useState<any[]>([]);
	const [isLoading, setIsLoading] = React.useState(true);
	const [error, setError] = React.useState<any>();

	function setCreditsafeId(creditsafeId) {
		dispatch(Utils.alertProgressMsg(t));

		Utils.executeStored({
			action: 'st_companies_update',
			dispatch,
			data: {
				comp_uid: props.compUid,
				comp_user_modified_uid: userData.user_uid,
				comp_creditsafe_id: creditsafeId
			},
			successHandler: () => {
				dispatch(Utils.alertSuccessMsg(t));
				props.setCreditsafeId(creditsafeId);
				dispatch(closeDialog());
				props.updateRefresh();
			}
		});
	}

	React.useEffect(() => {
		Utils.executeApi.get({
			url: `/api/creditsafe/companies?country=${props.country}&vatNumber=${props.vatNumber}`,
			dispatch,
			successHandler: response => {
				setCompanies(response.data.companies || []);
				setIsLoading(false);
			},
			errorHandler: ex => {
				setError(ex);
				setIsLoading(false);
			}
		});
	}, [dispatch, props.country, props.vatNumber]);

	return (
		<>
			<DialogTitle>{t('CREDITSAFE_DIALOG_SEARCH_TITLE')}</DialogTitle>
			<DialogContent>
				{error ? (
					<ErrorAlert error={error} />
				) : !isLoading ? (
					companies.length === 0 ? (
						<Typography className="p-16 italic" align="center" color="textSecondary">
							{t('LIST_NO_ITEMS')}
						</Typography>
					) : (
						<List dense disablePadding>
							{companies.map(company => (
								<ListItemButton key={company.id} onClick={() => setCreditsafeId(company.id)}>
									<ListItemText
										slotProps={{
											primary: { variant: 'subtitle2', fontWeight: '600' },
											secondary: { variant: 'subtitle2', color: 'text.secondary' }
										}}
										primary={company.name}
										secondary={company.address.simpleValue}
									/>
								</ListItemButton>
							))}
						</List>
					)
				) : (
					<LinearProgress variant="query" />
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
			</DialogActions>
		</>
	);
}
